import { warn } from '@execonline-inc/logging';
import { InvalidUrlError, URLParser, putQueryParam, toUrlT } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { Task } from 'taskarian';
import InvitationsStore from '.';
import { AppyError, callApi } from '../../../../../Appy';
import { currentUserStore } from '../../../../../CurrentUserStore';
import { handleError } from '../../../../../ErrorActionableReaction';
import { MissingLinkError, findLinkT } from '../../../../../LinkyLinky';
import { SearchParameters } from '../../../../../ListFilter/Types';
import ReactionComponent, { RCProps } from '../../../../../ReactionComponent';
import { Link } from '../../../../../Resource/Types';
import { invitationsResourceDecoder } from './Decoders';
import { InvitationsResource, Loading, LoadingMore, State } from './Types';

interface Props extends RCProps<InvitationsStore> {}

type InvitationsError = AppyError | InvalidUrlError | MissingLinkError;

const handleInvitationsError = (store: InvitationsStore) => (error: InvitationsError) => {
  switch (error.kind) {
    case 'missing-link-error':
    case 'invalid-url-error':
      store.error('We are unable to process your request at this time');
      warn('InvitationsReactions', error.kind, error);
      break;
    default:
      handleError(store, error);
  }
};

const sendRequest = callApi(invitationsResourceDecoder, {});

const updateSearchParams = (searchParameters: SearchParameters) => (url: URLParser) =>
  searchParameters.reduce(
    (acc, parameter) => putQueryParam(`invitations_filter[${parameter.key}]`, parameter.value)(acc),
    url,
  );

const buildUrl = (
  link: Link,
  state: Loading | LoadingMore,
  learningCollectionId: number,
): Task<InvitationsError, Link> =>
  toUrlT(link.href)
    .map(updateSearchParams(state.searchParameters))
    .map(putQueryParam('use_case_id', String(learningCollectionId)))
    .map(putQueryParam('page', String(state.page)))
    .map((url): Link => ({ ...link, href: url.href }));

const getInvitations = (store: InvitationsStore, state: Loading | LoadingMore) => (link: Link) =>
  buildUrl(link, state, store.learningCollectionId).andThen(sendRequest);

const concatInvitations =
  (state: LoadingMore) =>
  (newResource: InvitationsResource): InvitationsResource => ({
    ...newResource,
    payload: {
      ...state.invitationsResource.payload,
      invitations: state.invitationsResource.payload.invitations.concat(
        newResource.payload.invitations,
      ),
    },
  });

const requestInvitations = (store: InvitationsStore, state: Loading | LoadingMore) =>
  Task.succeed<InvitationsError, ReadonlyArray<Link>>(currentUserStore.links)
    .andThen(findLinkT('learning-collection-invitations'))
    .andThen(getInvitations(store, state));

class InvitationsReactions extends ReactionComponent<InvitationsStore, State, Props> {
  tester = () => this.props.store.state;
  effect = (state: State) => {
    const { store } = this.props;
    switch (state.kind) {
      case 'initializing':
        break;
      case 'loading':
        requestInvitations(store, state).fork(handleInvitationsError(store), store.ready);
        break;
      case 'loading-more':
        requestInvitations(store, state)
          .map(concatInvitations(state))
          .fork(handleInvitationsError(store), store.ready);
        break;
      case 'ready':
      case 'error':
        break;
      case 'updating-query':
        store.ready(state.invitationsResource);
        break;
      case 'updating-search-parameters':
      case 'sorting':
        store.loading(1);
        break;

      default:
        assertNever(state);
    }
  };
}

export default InvitationsReactions;
